"use client";

import Link from "next/link";
import { ChevronRightIcon } from "./icons";

type SectionHeaderProps = {
  title: string;
  viewAllHref?: string;
  viewAllLabel?: string;
  className?: string;
  /** Eyebrow above the title. Defaults to "Explore" for back-compat. */
  eyebrow?: string;
  /** Optional supporting line under the title. */
  description?: string;
};

/**
 * Left-aligned eyebrow + title (+ optional description) with a right-aligned
 * "View All ›" link, used above grid/list sections (e.g. the Explore
 * Reqrium grid).
 *
 * Premium pass (2026-08-04): the title now uses the shared
 * `.pr-section-title` scale so every section heading on the landing page
 * has identical size, weight, and tracking, and the "View All" control
 * reuses the `.pr-btn-ghost` pill so it matches the hero's secondary CTA.
 */
export default function SectionHeader({
  title,
  viewAllHref,
  viewAllLabel = "View All",
  className = "",
  eyebrow = "Explore",
  description,
}: SectionHeaderProps) {
  return (
    <div className={`flex flex-wrap items-end justify-between gap-4 ${className}`}>
      <div className="min-w-0">
        <span className="eyebrow-label">{eyebrow}</span>
        <h2 className="pr-section-title headline-balance mt-3">{title}</h2>
        {description ? <p className="pr-section-sub">{description}</p> : null}
      </div>
      {viewAllHref ? (
        <Link href={viewAllHref} className="pr-btn pr-btn-ghost pr-btn-sm group/viewall">
          <span>{viewAllLabel}</span>
          <ChevronRightIcon
            size={16}
            className="transition-transform duration-300 group-hover/viewall:translate-x-0.5"
          />
        </Link>
      ) : null}
    </div>
  );
}
